import type { Problem } from "./contract";

export type ErrorKind =
  | "auth" // 401 — session missing/expired
  | "csrf" // 403 csrf_invalid — refresh session then resubmit
  | "permission" // 403 — capability denied
  | "not_found"
  | "validation"
  | "conflict" // 409
  | "precondition" // 412 version_conflict
  | "precondition_required" // 428
  | "idempotency" // 409 idempotency_* replay/in-progress
  | "rate_limit" // 429
  | "unavailable" // 503 — entitlements/degraded
  | "network" // transport failure, never shows raw "Failed to fetch"
  | "generic";

/**
 * Normalized, typed error surfaced to the UI. We never leak transport detail or raw
 * HTTP status text — only the server-authored `detail`/`title` (the landing
 * `userMessage()` rule) plus a correlation id for support.
 */
export class AppError extends Error {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string;
  readonly code: string;
  readonly correlationId: string;
  readonly instance?: string;
  readonly retryAfterSeconds?: number;
  readonly fieldErrors?: Record<string, string>;
  /**
   * Scalar RFC 9457 extension members beyond the standard Problem fields, e.g.
   * `amountDue` on `quote_amount_changed` or `reason` on `subscription_not_modifiable`.
   */
  readonly extensions?: Readonly<Record<string, string | number | boolean | null>>;

  constructor(init: {
    type?: string;
    title: string;
    status: number;
    detail: string;
    code: string;
    correlationId: string;
    instance?: string;
    retryAfterSeconds?: number;
    fieldErrors?: Record<string, string>;
    extensions?: Readonly<Record<string, string | number | boolean | null>>;
    cause?: unknown;
  }) {
    super(init.detail || init.title, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = "AppError";
    this.type = init.type ?? "about:blank";
    this.title = init.title;
    this.status = init.status;
    this.detail = init.detail;
    this.code = init.code;
    this.correlationId = init.correlationId;
    if (init.instance !== undefined) this.instance = init.instance;
    if (init.retryAfterSeconds !== undefined) this.retryAfterSeconds = init.retryAfterSeconds;
    if (init.fieldErrors !== undefined) this.fieldErrors = init.fieldErrors;
    if (init.extensions !== undefined) this.extensions = init.extensions;
  }

  get kind(): ErrorKind {
    return intentFor(this.code, this.status);
  }

  /** Safe, user-facing message. Falls back to a generic line, never a stack/status. */
  get userMessage(): string {
    return this.detail || this.title || GENERIC_MESSAGE;
  }
}

export const GENERIC_MESSAGE = "Something went wrong. Please try again.";
const NETWORK_MESSAGE = "We couldn't reach the server. Check your connection and try again.";

export function intentFor(code: string, status: number): ErrorKind {
  switch (code) {
    case "csrf_invalid":
      return "csrf";
    case "version_conflict":
      return "precondition";
    case "precondition_required":
      return "precondition_required";
    case "idempotency_key_reused":
    case "idempotency_in_progress":
      return "idempotency";
    case "network_error":
      return "network";
    default:
      break;
  }
  if (status === 401) return "auth";
  if (status === 403) return "permission";
  if (status === 404) return "not_found";
  if (status === 412) return "precondition";
  if (status === 428) return "precondition_required";
  if (status === 429) return "rate_limit";
  if (status === 409) return "conflict";
  if (status === 400) return "validation";
  if (status === 503) return "unavailable";
  return "generic";
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Problem).code === "string" &&
    typeof (value as Problem).status === "number"
  );
}

function toFieldErrors(value: unknown): Record<string, string> | undefined {
  // The v1 contract's Problem has no field-error extension, but if the API ever
  // returns an `errors` map we map it to per-field messages defensively.
  if (typeof value !== "object" || value === null) return undefined;
  const raw = (value as { errors?: unknown }).errors;
  if (typeof raw !== "object" || raw === null) return undefined;
  const out: Record<string, string> = {};
  for (const [key, msg] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof msg === "string") out[key] = msg;
    else if (Array.isArray(msg) && typeof msg[0] === "string") out[key] = msg[0];
  }
  return Object.keys(out).length ? out : undefined;
}

const STANDARD_PROBLEM_MEMBERS = new Set(["type", "title", "status", "detail", "instance", "code", "requestId", "fieldErrors", "errors"]);

function toExtensions(value: object): Record<string, string | number | boolean | null> | undefined {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, member] of Object.entries(value)) {
    if (STANDARD_PROBLEM_MEMBERS.has(key)) continue;
    if (member === null || typeof member === "string" || typeof member === "number" || typeof member === "boolean") out[key] = member;
  }
  return Object.keys(out).length ? out : undefined;
}

export function problemToError(
  body: unknown,
  fallbackStatus: number,
  requestId: string,
  retryAfterSeconds?: number
): AppError {
  if (isProblem(body)) {
    const fieldErrors = toFieldErrors(body);
    const extensions = toExtensions(body);
    return new AppError({
      type: body.type,
      title: body.title,
      status: body.status,
      detail: body.detail,
      code: body.code,
      correlationId: body.requestId || requestId,
      ...(body.instance !== undefined ? { instance: body.instance } : {}),
      ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
      ...(fieldErrors ? { fieldErrors } : {}),
      ...(extensions ? { extensions } : {})
    });
  }
  return new AppError({
    title: "Request failed",
    status: fallbackStatus,
    detail: GENERIC_MESSAGE,
    code: "unexpected_response",
    correlationId: requestId,
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {})
  });
}

/**
 * Anything a call site threw, as an AppError. Transport and API failures already
 * arrive as AppError; this is the net for everything else — a bug in a handler, a
 * serialization failure — so a caller renders a correlated banner instead of
 * swallowing the failure silently or leaking an unhandled rejection. The original
 * value is kept as `cause` for the console; it is never shown to the operator.
 */
export function unexpectedError(cause: unknown, correlationId?: string): AppError {
  if (cause instanceof AppError) return cause;
  return new AppError({
    title: "Unexpected error",
    status: 0,
    detail: GENERIC_MESSAGE,
    code: "unexpected_error",
    correlationId: correlationId ?? crypto.randomUUID(),
    cause
  });
}

export function networkError(correlationId: string): AppError {
  return new AppError({
    title: "Connection problem",
    status: 0,
    detail: NETWORK_MESSAGE,
    code: "network_error",
    correlationId
  });
}
